import React, { useState, useEffect } from 'react';
import { User, WorkOrder } from '../types';
import { db } from '../services/db';
import { PageHeader } from './ui/PageHeader';
import { Button } from './ui/Button';
import { DollarSign, Calendar, TrendingUp, AlertTriangle, Users, FileText, ShoppingCart, Download, HelpCircle, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { getStatusLabel } from '../services/mockData';
import { UpgradeRequired } from './ui/UpgradeRequired';
import { ConfirmModal } from './ui/ConfirmModal';

interface PainelFaturamentoProps {
  currentUser: User;
  orders: WorkOrder[];
  currentCompany: any;
  allUsers: User[];
  onChangeView: (view: string) => void;
}

export const PainelFaturamento: React.FC<PainelFaturamentoProps> = ({
  currentUser,
  orders,
  currentCompany,
  allUsers = [],
  onChangeView
}) => {
  const [confirmModal, setConfirmModal] = useState<{
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmStyle?: 'danger' | 'primary';
    onConfirm: () => void;
    onCancel?: () => void;
  } | null>(null);

  const [period, setPeriod] = useState<'semanal' | 'mensal' | 'anual'>('mensal');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filterTecnico, setFilterTecnico] = useState('todos');
  const [filterTipo, setFilterTipo] = useState('todos');
  const [expenses, setExpenses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const data = await db.getFinancialSummary();
    setExpenses(data.expenses || []);
    setLoading(false);
  };

  const isPro = ['pro', 'business'].includes(currentCompany?.plano?.toLowerCase());
  
  if (!isPro) {
    return (
      <UpgradeRequired
        featureName="Painel de Faturamento"
        requiredPlan="Pro"
        onUpgrade={() => onChangeView('UPGRADE')}
      />
    );
  }

  // --- LÓGICA DE CÁLCULO ---

  // Filtrar OS por período selecionado
  const getOrdersInPeriod = (ordersToFilter: WorkOrder[]) => {
    return ordersToFilter.filter(order => {
      if (!order.suggestedDate) return false;
      const date = new Date(order.suggestedDate);
      if (period === 'mensal') {
        return date.getMonth() === selectedMonth && date.getFullYear() === selectedYear;
      }
      if (period === 'semanal') {
        const now = new Date();
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay());
        weekStart.setHours(0,0,0,0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return date >= weekStart && date <= weekEnd;
      }
      if (period === 'anual') {
        return date.getFullYear() === selectedYear;
      }
      return true;
    });
  };

  // OS concluídas no período
  const completedOrders = getOrdersInPeriod(
    orders.filter(o => o.status === 'COMPLETED' && 
      (filterTecnico === 'todos' || o.technicianId === filterTecnico || o.environments?.some(e => e.technicianId === filterTecnico)) &&
      (filterTipo === 'todos' || o.type === filterTipo)
    )
  );

  // Calcular valor bruto de uma OS (soma dos ambientes)
  const getOrderValue = (order: WorkOrder) =>
    order.environments?.reduce((sum, env) => sum + (env.value_brl || 0), 0) || 0;

  // Calcular gastos de uma OS
  const getOrderExpenses = (orderId: string) =>
    expenses.filter(e => e.order_id === orderId).reduce((sum, e) => sum + (e.total_value || 0), 0);

  // Faturamento bruto
  const faturamentoBruto = completedOrders.reduce((sum, o) => sum + getOrderValue(o), 0);

  // Total de gastos
  const totalGastos = completedOrders.reduce((sum, o) => sum + getOrderExpenses(o.id), 0);

  // Faturamento líquido
  const faturamentoLiquido = faturamentoBruto - totalGastos;

  // OS em andamento e agendadas (previsão)
  const forecastOrders = orders.filter(o =>
    ['OPEN', 'ASSIGNED', 'IN_PROGRESS'].includes(o.status) &&
    o.type === 'ASSEMBLY' &&
    o.suggestedDate
  ).sort((a, b) => new Date(a.suggestedDate!).getTime() - new Date(b.suggestedDate!).getTime());

  // Previsão esta semana
  const now = new Date();
  const weekEnd = new Date(now);
  weekEnd.setDate(now.getDate() + 7);
  const forecastWeek = forecastOrders
    .filter(o => new Date(o.suggestedDate!) <= weekEnd)
    .reduce((sum, o) => sum + getOrderValue(o), 0);

  // Previsão próximos 30 dias
  const next30 = new Date(now);
  next30.setDate(now.getDate() + 30);
  const forecast30 = forecastOrders
    .filter(o => new Date(o.suggestedDate!) <= next30)
    .reduce((sum, o) => sum + getOrderValue(o), 0);

  // Estimativa próximo mês (forecast para o próximo mês do calendário)
  const nextMonthYear = selectedMonth === 11 ? selectedYear + 1 : selectedYear;
  const nextMonth = selectedMonth === 11 ? 0 : selectedMonth + 1;
  const forecastNextMonth = forecastOrders
    .filter(o => {
      const od = new Date(o.suggestedDate!);
      return od.getMonth() === nextMonth && od.getFullYear() === nextMonthYear;
    })
    .reduce((sum, o) => sum + getOrderValue(o), 0);

  // Faturamento por técnico
  const byTechnician = allUsers
    .filter(u => u.role === 'TECHNICIAN')
    .map(tech => {
      const techOrders = completedOrders.filter(o =>
        o.technicianId === tech.id || o.environments?.some(e => e.technicianId === tech.id)
      );
      const bruto = techOrders.reduce((sum, o) => sum + getOrderValue(o), 0);
      const gastos = techOrders.reduce((sum, o) => sum + getOrderExpenses(o.id), 0);
      return { tech, count: techOrders.length, bruto, liquido: bruto - gastos };
    })
    .filter(t => t.count > 0)
    .sort((a, b) => b.bruto - a.bruto);

  // Faturamento por tipo
  const montagens = completedOrders.filter(o => o.type === 'ASSEMBLY');
  const assistencias = completedOrders.filter(o => o.type === 'ASSISTANCE');
  const bruto_montagens = montagens.reduce((sum, o) => sum + getOrderValue(o), 0);
  const bruto_assistencias = assistencias.reduce((sum, o) => sum + getOrderValue(o), 0);

  // Dados para o gráfico anual (últimos 12 meses)
  const chartData = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(selectedYear, i, 1);
    const monthOrders = orders.filter(o => {
      if (!o.suggestedDate || o.status !== 'COMPLETED') return false;
      const od = new Date(o.suggestedDate);
      return od.getMonth() === i && od.getFullYear() === selectedYear;
    });
    const bruto = monthOrders.reduce((sum, o) => sum + getOrderValue(o), 0);
    const gastos = monthOrders.reduce((sum, o) => sum + getOrderExpenses(o.id), 0);
    const isFuture = d > new Date();
    const forecastVal = isFuture ? forecastOrders
      .filter(o => { const od = new Date(o.suggestedDate!); return od.getMonth() === i && od.getFullYear() === selectedYear; })
      .reduce((sum, o) => sum + getOrderValue(o), 0) : 0;
    return {
      label: d.toLocaleString('pt-BR', { month: 'short' }).replace('.', '').substring(0, 3).toUpperCase(),
      bruto: isFuture ? forecastVal : bruto,
      liquido: isFuture ? forecastVal * 0.85 : bruto - gastos,
      gastos: isFuture ? 0 : gastos,
      previsto: isFuture,
    };
  });

  // --- COMPARAÇÃO VS PERÍODO ANTERIOR (Ex: Mês anterior) ---
  const getPrevMonthOrders = () => {
    const prevMonth = selectedMonth === 0 ? 11 : selectedMonth - 1;
    const prevYear = selectedMonth === 0 ? selectedYear - 1 : selectedYear;
    return orders.filter(o => {
      if (!o.suggestedDate || o.status !== 'COMPLETED') return false;
      const date = new Date(o.suggestedDate);
      const matchesTech = filterTecnico === 'todos' || o.technicianId === filterTecnico || o.environments?.some(e => e.technicianId === filterTecnico);
      const matchesType = filterTipo === 'todos' || o.type === filterTipo;
      return date.getMonth() === prevMonth && date.getFullYear() === prevYear && matchesTech && matchesType;
    });
  };

  const prevOrders = getPrevMonthOrders();
  const prevBruto = prevOrders.reduce((sum, o) => sum + getOrderValue(o), 0);
  const prevGastos = prevOrders.reduce((sum, o) => sum + getOrderExpenses(o.id), 0);

  const getVariaçao = (atual: number, anterior: number) => {
    if (anterior === 0) return { label: atual > 0 ? '+100%' : '0%', isPositive: true, neutral: atual === 0 };
    const diff = ((atual - anterior) / anterior) * 100;
    return {
      label: `${diff >= 0 ? '+' : ''}${diff.toFixed(1)}%`,
      isPositive: diff >= 0,
      neutral: false
    };
  };

  const varBruto = getVariaçao(faturamentoBruto, prevBruto);
  const varGastos = getVariaçao(totalGastos, prevGastos);

  // Formatação
  const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const fmtK = (v: number) => v >= 1000 ? `R$ ${(v/1000).toFixed(0)}k` : fmt(v);

  // Função para exportar CSV
  const exportCSV = () => {
    if (completedOrders.length === 0) {
      setConfirmModal({
        title: 'Sem Dados',
        message: 'Nenhum dado para exportar neste período.',
        onConfirm: () => setConfirmModal(null)
      });
      return;
    }
    const headers = ['ID da OS', 'Cliente', 'Tipo', 'Técnico', 'Data Prevista', 'Status', 'Valor Bruto', 'Gastos', 'Valor Líquido'];
    const rows = completedOrders.map(o => {
      const bruto = getOrderValue(o);
      const gastos = getOrderExpenses(o.id);
      const liquido = bruto - gastos;
      return [
        o.id,
        o.clientName,
        o.type === 'ASSEMBLY' ? 'Montagem' : 'Assistência',
        o.technicianName || 'Não atribuído',
        o.suggestedDate ? new Date(o.suggestedDate).toLocaleDateString('pt-BR') : '',
        o.status,
        bruto.toFixed(2),
        gastos.toFixed(2),
        liquido.toFixed(2)
      ];
    });

    const csvContent = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `faturamento_${period}_${selectedMonth + 1}_${selectedYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const monthsList = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const yearsList = [2024, 2025, 2026, 2027];

  // Cálculo de altura máxima do gráfico
  const maxValGraph = Math.max(
    ...chartData.map(d => Math.max(d.bruto, d.liquido, d.gastos)),
    5000 // Valor mínimo para evitar divisão por zero se vazio
  );

  return (
    <div className="space-y-6 animate-fade-in pb-12">
      {/* Topbar */}
      <PageHeader
        title="Painel de Faturamento"
        description="Faturamento realizado, previsto e fluxo por período"
        icon={<DollarSign size={24} style={{ color: 'var(--cyan)' }} />}
        actionSecondary={
          <Button
            variant="secondary"
            onClick={exportCSV}
            disabled={completedOrders.length === 0}
            icon={<Download size={18} />}
          >
            Exportar
          </Button>
        }
      />

      {/* Tabs de período */}
      <div className="flex bg-navy2 p-1.5 rounded-2xl border border-white/5 w-fit gap-2">
        <button
          onClick={() => setPeriod('semanal')}
          className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
            period === 'semanal'
              ? 'bg-blue text-white shadow-lg shadow-blue/20'
              : 'text-slate-400 hover:text-white hover:bg-white/5'
          }`}
        >
          Semanal
        </button>
        <button
          onClick={() => setPeriod('mensal')}
          className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
            period === 'mensal'
              ? 'bg-blue text-white shadow-lg shadow-blue/20'
              : 'text-slate-400 hover:text-white hover:bg-white/5'
          }`}
        >
          Mensal
        </button>
        <button
          onClick={() => setPeriod('anual')}
          className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
            period === 'anual'
              ? 'bg-blue text-white shadow-lg shadow-blue/20'
              : 'text-slate-400 hover:text-white hover:bg-white/5'
          }`}
        >
          Acumulado no ano
        </button>
      </div>

      {/* Filtros */}
      <div className="card p-6 rounded-3xl grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {(period === 'mensal' || period === 'semanal') && (
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Mês</label>
            <select
              className="input-field w-full h-11"
              value={selectedMonth}
              onChange={e => setSelectedMonth(Number(e.target.value))}
            >
              {monthsList.map((m, idx) => (
                <option key={m} value={idx}>{m}</option>
              ))}
            </select>
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Ano</label>
          <select
            className="input-field w-full h-11"
            value={selectedYear}
            onChange={e => setSelectedYear(Number(e.target.value))}
          >
            {yearsList.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Técnico</label>
          <select
            className="input-field w-full h-11"
            value={filterTecnico}
            onChange={e => setFilterTecnico(e.target.value)}
          >
            <option value="todos">Todos os técnicos</option>
            {allUsers
              .filter(u => u.role === 'TECHNICIAN')
              .map(tech => (
                <option key={tech.id} value={tech.id}>{tech.name}</option>
              ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Tipo de O.S.</label>
          <select
            className="input-field w-full h-11"
            value={filterTipo}
            onChange={e => setFilterTipo(e.target.value)}
          >
            <option value="todos">Todos</option>
            <option value="ASSEMBLY">Montagem</option>
            <option value="ASSISTANCE">Assistência</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="card p-12 text-center text-slate-400">
          Carregando dados financeiros...
        </div>
      ) : (
        <>
          {/* Cards de métricas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card 1 - Faturamento bruto */}
            <div className="card p-5 rounded-3xl flex flex-col justify-between relative overflow-hidden" style={{ borderTop: '3px solid var(--blue)' }}>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Faturamento Bruto</span>
                <h3 className="text-3xl font-black text-white mt-2">{fmt(faturamentoBruto)}</h3>
              </div>
              <div className="flex items-center gap-1.5 mt-4 text-xs">
                {period === 'mensal' && !varBruto.neutral ? (
                  <>
                    <span className="flex items-center gap-0.5 font-bold" style={{ color: varBruto.isPositive ? 'var(--sf-green)' : 'var(--sf-red)' }}>
                      {varBruto.isPositive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                      {varBruto.label}
                    </span>
                    <span className="text-slate-400">vs mês anterior</span>
                  </>
                ) : (
                  <span className="text-slate-400">Bruto no período</span>
                )}
              </div>
            </div>

            {/* Card 2 - Faturamento líquido */}
            <div className="card p-5 rounded-3xl flex flex-col justify-between relative overflow-hidden" style={{ borderTop: '3px solid var(--sf-green)' }}>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Faturamento Líquido</span>
                <h3 className="text-3xl font-black text-white mt-2" style={{ color: 'var(--sf-green)' }}>{fmt(faturamentoLiquido)}</h3>
              </div>
              <div className="flex items-center gap-1.5 mt-4 text-xs text-slate-400">
                <span>Após descontar gastos de O.S.</span>
              </div>
            </div>

            {/* Card 3 - Gastos com materiais */}
            <div className="card p-5 rounded-3xl flex flex-col justify-between relative overflow-hidden" style={{ borderTop: '3px solid var(--sf-red)' }}>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Gastos com Materiais</span>
                <h3 className="text-3xl font-black mt-2" style={{ color: 'var(--sf-red)' }}>{fmt(totalGastos)}</h3>
              </div>
              <div className="flex items-center gap-1.5 mt-4 text-xs">
                {period === 'mensal' && !varGastos.neutral ? (
                  <>
                    <span className="flex items-center gap-0.5 font-bold" style={{ color: !varGastos.isPositive ? 'var(--sf-green)' : 'var(--sf-red)' }}>
                      {varGastos.isPositive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                      {varGastos.label}
                    </span>
                    <span className="text-slate-400">vs mês anterior</span>
                  </>
                ) : (
                  <span className="text-slate-400">Materiais e ferramentas</span>
                )}
              </div>
            </div>

            {/* Card 4 - O.S. Concluídas */}
            <div className="card p-5 rounded-3xl flex flex-col justify-between relative overflow-hidden" style={{ borderTop: '3px solid #fbbf24' }}>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">O.S. Concluídas</span>
                <h3 className="text-3xl font-black text-white mt-2" style={{ color: '#fbbf24' }}>{completedOrders.length}</h3>
              </div>
              <div className="flex items-center gap-1.5 mt-4 text-xs text-slate-400">
                <span>Concluídas no período</span>
              </div>
            </div>
          </div>

          {/* Gráfico de evolução anual */}
          <div className="card p-6 rounded-3xl flex flex-col gap-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h3 className="font-bold text-[16px] text-slate-200">Evolução de Fluxo Anual ({selectedYear})</h3>
                <p className="text-xs text-slate-400">Comparativo mensal entre valores bruto, líquido, gastos realizados e estimativas</p>
              </div>
              
              {/* Legenda */}
              <div className="flex flex-wrap gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--blue)' }} />
                  <span className="text-slate-300">Bruto</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--sf-green)' }} />
                  <span className="text-slate-300">Líquido</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--sf-red)' }} />
                  <span className="text-slate-300">Gastos</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: 'transparent', border: '1.5px dashed var(--cyan)' }} />
                  <span className="text-slate-300">Previsto</span>
                </div>
              </div>
            </div>

            {/* Custom pure-HTML Graph */}
            <div className="flex flex-col gap-4 mt-2 annual-flow-chart-container">
              <div className="flex h-64 relative border-b border-white/10 pb-2">
                {/* Eixo Y */}
                <div className="flex flex-col justify-between text-[10px] text-slate-400 font-bold w-12 pr-2 select-none h-full pb-4">
                  <span>{fmtK(maxValGraph)}</span>
                  <span>{fmtK(maxValGraph * 0.75)}</span>
                  <span>{fmtK(maxValGraph * 0.5)}</span>
                  <span>{fmtK(maxValGraph * 0.25)}</span>
                  <span>R$ 0</span>
                </div>

                {/* Eixo X + Linhas do Graph */}
                <div className="flex-1 flex justify-between items-end h-full px-2 relative">
                  {/* Linhas de grade de fundo */}
                  <div className="absolute inset-0 flex flex-col justify-between pointer-events-none pb-4">
                    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', width: '100%', height: 0 }} />
                    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', width: '100%', height: 0 }} />
                    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', width: '100%', height: 0 }} />
                    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', width: '100%', height: 0 }} />
                  </div>

                  {chartData.map((d, index) => {
                    const hBruto = `${Math.max(2, (d.bruto / maxValGraph) * 85)}%`;
                    const hLiquido = `${Math.max(2, (d.liquido / maxValGraph) * 85)}%`;
                    const hGastos = `${Math.max(2, (d.gastos / maxValGraph) * 85)}%`;

                    return (
                      <div key={index} className="flex-1 flex flex-col items-center justify-end h-full px-1 z-10 group relative">
                        {/* Tooltip Hover */}
                        <div className="absolute bottom-full mb-2 bg-navy3 border border-white/10 px-3 py-2 rounded-xl text-[10px] leading-relaxed hidden group-hover:block z-50 shadow-2xl">
                          <p className="font-bold text-white mb-1 uppercase tracking-wider">{d.label} {d.previsto && '(Previsto)'}</p>
                          <p className="text-slate-300">Bruto: <span className="text-white font-bold">{fmt(d.bruto)}</span></p>
                          <p className="text-slate-300">Líquido: <span className="text-white font-bold">{fmt(d.liquido)}</span></p>
                          <p className="text-slate-300">Gastos: <span className="text-white font-bold">{fmt(d.gastos)}</span></p>
                        </div>

                        {/* Barras do mês */}
                        <div className="flex items-end gap-1 w-full justify-center max-w-[48px] h-full pb-2">
                          {/* Bruto */}
                          <div
                            style={{
                              height: hBruto,
                              background: d.previsto ? 'rgba(0,212,255,0.1)' : 'var(--blue)',
                              border: d.previsto ? '1.5px dashed var(--cyan)' : 'none',
                              borderRadius: '3px 3px 0 0',
                              opacity: d.previsto ? 0.6 : 1,
                              width: '28%',
                              transition: 'height 0.3s ease'
                            }}
                          />
                          {/* Líquido */}
                          <div
                            style={{
                              height: hLiquido,
                              background: d.previsto ? 'rgba(16,185,129,0.1)' : 'var(--sf-green)',
                              border: d.previsto ? '1.5px dashed var(--sf-green)' : 'none',
                              borderRadius: '3px 3px 0 0',
                              opacity: d.previsto ? 0.6 : 1,
                              width: '28%',
                              transition: 'height 0.3s ease'
                            }}
                          />
                          {/* Gastos */}
                          <div
                            style={{
                              height: hGastos,
                              background: 'var(--sf-red)',
                              borderRadius: '3px 3px 0 0',
                              width: '28%',
                              transition: 'height 0.3s ease'
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Rótulos de Meses */}
              <div className="flex justify-between items-center pl-12 text-[10px] text-slate-400 font-bold select-none uppercase tracking-wider px-2 annual-flow-chart-x-labels">
                {chartData.map((d, index) => (
                  <span key={index} className="flex-1 text-center">
                    {d.label}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Grid inferior (2 colunas) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Coluna 1 - Por técnico */}
            <div className="card rounded-3xl overflow-hidden flex flex-col">
              <div className="p-6 border-b border-white/5 flex items-center gap-2">
                <Users size={18} style={{ color: 'var(--sf-blue)' }} />
                <h3 className="font-bold text-[16px] text-slate-200">Faturamento por Técnico</h3>
              </div>
              {byTechnician.length === 0 ? (
                <div className="p-12 text-center text-slate-400">
                  Nenhum faturamento registrado por técnico neste período.
                </div>
              ) : (
                <div className="overflow-x-auto flex-1">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-navy4/30 border-b border-white/5">
                        <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">TÉCNICO</th>
                        <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-center" style={{ width: 80 }}>O.S.</th>
                        <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-right" style={{ width: 130 }}>BRUTO</th>
                        <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-right" style={{ width: 130 }}>LÍQUIDO</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {byTechnician.map(t => (
                        <tr key={t.tech.id} className="hover:bg-white/5 transition-colors">
                          <td className="p-4 flex items-center gap-3">
                            <img
                              src={t.tech.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(t.tech.name)}&background=1a6bff&color=fff`}
                              alt={t.tech.name}
                              className="w-8 h-8 rounded-full object-cover border border-white/10"
                            />
                            <div>
                              <p className="font-bold text-white text-sm">{t.tech.name}</p>
                              <p className="text-[10px] text-slate-400 uppercase tracking-widest">{t.tech.classification || 'Técnico'}</p>
                            </div>
                          </td>
                          <td className="p-4 text-center font-bold text-white text-sm">{t.count}</td>
                          <td className="p-4 text-right font-semibold text-white text-sm">{fmt(t.bruto)}</td>
                          <td className="p-4 text-right font-bold text-sm" style={{ color: 'var(--sf-green)' }}>{fmt(t.liquido)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-navy4/40 font-bold border-t border-white/10">
                        <td className="p-4 text-sm text-slate-300">Total</td>
                        <td className="p-4 text-center text-sm text-white">{byTechnician.reduce((sum, t) => sum + t.count, 0)}</td>
                        <td className="p-4 text-right text-sm text-white">{fmt(byTechnician.reduce((sum, t) => sum + t.bruto, 0))}</td>
                        <td className="p-4 text-right text-sm" style={{ color: 'var(--sf-green)' }}>{fmt(byTechnician.reduce((sum, t) => sum + t.liquido, 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* Coluna 2 - Por tipo de OS */}
            <div className="card rounded-3xl overflow-hidden flex flex-col">
              <div className="p-6 border-b border-white/5 flex items-center gap-2">
                <FileText size={18} style={{ color: 'var(--sf-blue)' }} />
                <h3 className="font-bold text-[16px] text-slate-200">Faturamento por Tipo de O.S.</h3>
              </div>
              <div className="p-6 flex-1 flex flex-col justify-between gap-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-navy4/30 border-b border-white/5">
                        <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">TIPO DE SERVIÇO</th>
                        <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-center" style={{ width: 80 }}>O.S.</th>
                        <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-right" style={{ width: 130 }}>BRUTO</th>
                        <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-right" style={{ width: 100 }}>PARTICIP.</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      <tr className="hover:bg-white/5 transition-colors">
                        <td className="p-4 text-sm font-semibold text-white">Montagens</td>
                        <td className="p-4 text-center text-sm font-bold text-white">{montagens.length}</td>
                        <td className="p-4 text-right text-sm font-semibold text-white">{fmt(bruto_montagens)}</td>
                        <td className="p-4 text-right text-sm text-slate-300">
                          {faturamentoBruto > 0 ? `${((bruto_montagens / faturamentoBruto) * 100).toFixed(0)}%` : '0%'}
                        </td>
                      </tr>
                      <tr className="hover:bg-white/5 transition-colors">
                        <td className="p-4 text-sm font-semibold text-white">Assistências</td>
                        <td className="p-4 text-center text-sm font-bold text-white">{assistencias.length}</td>
                        <td className="p-4 text-right text-sm font-semibold text-white">{fmt(bruto_assistencias)}</td>
                        <td className="p-4 text-right text-sm text-slate-300">
                          {faturamentoBruto > 0 ? `${((bruto_assistencias / faturamentoBruto) * 100).toFixed(0)}%` : '0%'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Progress bars */}
                <div className="space-y-4 pt-4 border-t border-white/5">
                  <div>
                    <div className="flex justify-between text-xs font-semibold mb-1 text-slate-300">
                      <span>Montagens</span>
                      <span>{faturamentoBruto > 0 ? `${((bruto_montagens / faturamentoBruto) * 100).toFixed(1)}%` : '0%'}</span>
                    </div>
                    <div className="w-full bg-white/5 h-2 rounded-full overflow-hidden">
                      <div
                        className="bg-blue h-full transition-all duration-500"
                        style={{ width: faturamentoBruto > 0 ? `${(bruto_montagens / faturamentoBruto) * 100}%` : '0%' }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-xs font-semibold mb-1 text-slate-300">
                      <span>Assistências</span>
                      <span>{faturamentoBruto > 0 ? `${((bruto_assistencias / faturamentoBruto) * 100).toFixed(1)}%` : '0%'}</span>
                    </div>
                    <div className="w-full bg-white/5 h-2 rounded-full overflow-hidden">
                      <div
                        className="bg-cyan h-full transition-all duration-500"
                        style={{ width: faturamentoBruto > 0 ? `${(bruto_assistencias / faturamentoBruto) * 100}%` : '0%' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Previsão de recebimentos */}
          <div className="card p-6 rounded-3xl flex flex-col gap-6">
            <div>
              <h3 className="font-bold text-[16px] text-slate-200">Previsão de Recebimentos futuros</h3>
              <p className="text-xs text-slate-400">Estimativas baseadas em montagens agendadas, em andamento ou abertas</p>
            </div>

            {/* 3 Métricas no topo */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/5 border border-white/5 p-4 rounded-2xl flex flex-col justify-center">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Previsão esta semana</span>
                <span className="text-xl font-bold text-white mt-1">{fmt(forecastWeek)}</span>
              </div>
              <div className="bg-white/5 border border-white/5 p-4 rounded-2xl flex flex-col justify-center">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Próximos 30 dias</span>
                <span className="text-xl font-bold text-white mt-1">{fmt(forecast30)}</span>
              </div>
              <div className="bg-white/5 border border-white/5 p-4 rounded-2xl flex flex-col justify-center">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Estimativa próximo mês</span>
                <span className="text-xl font-bold text-white mt-1">{fmt(forecastNextMonth)}</span>
              </div>
            </div>

            {/* Próximas OS agendadas */}
            <div className="space-y-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block">Próximas O.S. agendadas (Max 5)</span>
              
              {forecastOrders.length === 0 ? (
                <div className="text-center py-6 text-slate-500 text-sm">
                  Nenhuma ordem prevista para os próximos períodos.
                </div>
              ) : (
                <div className="space-y-2">
                  {forecastOrders.slice(0, 5).map(o => {
                    const val = getOrderValue(o);
                    const envsListStr = o.environments?.map(e => e.name).join(', ') || 'Nenhum ambiente';

                    return (
                      <div
                        key={o.id}
                        style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.05)' }}
                        className="p-4 rounded-2xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 hover:border-white/10 transition-colors"
                      >
                        <div>
                          <div className="flex items-center gap-2 flex-wrap upcoming-os-row">
                            <span className="text-xs font-black text-cyan uppercase">
                              {o.id.toUpperCase().startsWith('OS-') ? o.id.toUpperCase() : `OS-${o.id.substring(0, 8)}`}
                            </span>
                            <span className="text-slate-400 text-xs">•</span>
                            <span className="text-xs font-bold text-white">{o.clientName}</span>
                            <span className="upcoming-os-status-badge" style={{ fontSize: 9, background: 'rgba(26,107,255,0.15)', color: '#60a5fa', padding: '1px 6px', borderRadius: 4, fontWeight: 'bold', whiteSpace: 'nowrap', flexShrink: 0 }}>
                              {getStatusLabel(o.status)}
                            </span>
                          </div>
                          <p className="text-xs text-slate-400 mt-1 truncate max-w-md">
                            {envsListStr}
                          </p>
                        </div>
                        <div className="flex sm:flex-col items-end justify-between sm:justify-center w-full sm:w-auto border-t sm:border-t-0 border-white/5 pt-2 sm:pt-0">
                          <span className="text-[11px] text-slate-400 sm:mb-1">
                            {o.suggestedDate ? new Date(o.suggestedDate).toLocaleDateString('pt-BR') : 'Sem data'}
                          </span>
                          <span className="text-sm font-black text-white">{fmt(val)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {confirmModal && (
        <ConfirmModal
          open={!!confirmModal}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          cancelLabel={confirmModal.cancelLabel}
          confirmStyle={confirmModal.confirmStyle}
          onConfirm={confirmModal.onConfirm}
          onCancel={confirmModal.onCancel}
        />
      )}
    </div>
  );
};
